// What Classify and Import accept as an attachment, and the limits.
//
// Mirrors the server's own rules (pyapi/app/services/classify_tool/attachment_service.py, which applies the
// same file-type and size rules as the Design Assistant: an allow-listed content type OR a known extension,
// PDF up to 40 MB, image up to 20 MB), so the box refuses at once what the server would refuse at send time.
// Without the check, an unsupported file only failed on Send and then stayed stuck in the box.
//
// The limits themselves (files per message, pages per PDF, sizes) are the server's to set: the page fetches
// the ones in force (classifyAttachmentLimits) and falls back to DEFAULT_ATTACHMENT_LIMITS, which are what the
// server enforces unless an operator has changed them. Pages cannot be counted here, so a long PDF is refused
// by the server on Send; the files and sizes are checked at once.
//
// There are two size limits and they are not the same. 40 MB / 20 MB is what the server will STORE; what one
// message can carry is what the MODEL can be sent (maxMessageBytes, about 21 MB of files): a PDF travels as
// base64 in one request, and measured against the live API a larger one fails (pyapi/app/services/shared/
// model_request_budget.py has the numbers). The second is the one a user can actually hit, so it is the one
// the box checks, per file and as a running total, and the one the hint states.
//
// Every message says what is wrong, the limit, and what to change, and names the file where more than one
// could be meant. The server's refusals (pyapi/app/services/classify_tool/attachment_limits.py) read the same.
//
// Deliberately its own copy rather than an import from the Design Assistant (components/ExportDesign): the
// two tools' attachment storage is separate, and neither should reach into the other.

export const MAX_PDF_BYTES = 40 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

// A photo up to this size is sent to the model as it is; a bigger one is shrunk first (the server's
// _MAX_IMAGE_BYTES_FOR_MODEL), so what it adds to a message is small and not known here.
export const MODEL_IMAGE_PASS_THROUGH_BYTES = 7_500_000;

export interface AttachmentLimits {
  maxFilesPerTurn: number;
  maxPdfPages: number;
  maxPdfBytes: number;
  maxImageBytes: number;
  // How much file data one message can carry before the model cannot take it.
  maxMessageBytes: number;
}

export const DEFAULT_ATTACHMENT_LIMITS: AttachmentLimits = {
  maxFilesPerTurn: 3,
  maxPdfPages: 20,
  maxPdfBytes: MAX_PDF_BYTES,
  maxImageBytes: MAX_IMAGE_BYTES,
  maxMessageBytes: 22_875_000,
};

export const ACCEPTED_ATTACHMENT_TYPES =
  'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg,image/webp,.webp';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const SUPPORTED_IMAGE_EXTENSION = /\.(png|jpe?g|webp)$/i;

type FileLike = { name: string; type: string; size?: number };

export const isPdfFile = (file: FileLike): boolean =>
  (file.type || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(file.name || '');

export const isSupportedImageFile = (file: FileLike): boolean =>
  SUPPORTED_IMAGE_TYPES.includes((file.type || '').toLowerCase()) ||
  SUPPORTED_IMAGE_EXTENSION.test(file.name || '');

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 KB';
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(kilobytes >= 10 ? 1 : 2)} KB`;
  }
  return `${(kilobytes / 1024).toFixed(2)} MB`;
};

/**
 * "24.3 MB" / "40 MB". A size is rounded UP to a tenth, so a file that is over a limit never reads as equal
 * to it ("20.0 MB is over 20 MB").
 */
export const formatMegabytes = (bytes: number): string => {
  const tenths = Math.ceil(Number(((bytes / (1024 * 1024)) * 10).toFixed(6)));
  const megabytes = tenths / 10;
  return Number.isInteger(megabytes) ? `${megabytes} MB` : `${megabytes.toFixed(1)} MB`;
};

const filesLabel = (count: number): string => `${count} file${count === 1 ? '' : 's'}`;

// "a.png", "a.png and b.png", "a.png, b.png and c.png", and a count once a list would run on.
const nameList = (names: string[]): string => {
  if (names.length > 3) return `${names.length} files`;
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export const isHeicFile = (file: FileLike): boolean =>
  /\.(heic|heif)$/i.test(file.name || '') || ['image/heic', 'image/heif'].includes((file.type || '').toLowerCase());

export const unsupportedTypeMessage = (file: FileLike): string =>
  isHeicFile(file)
    ? `${file.name}: iPhone HEIC photos can't be attached. Export it as JPEG or PNG and try again.`
    : `${file.name}: Only PDF, PNG, JPEG or WebP files can be attached. Convert it and try again.`;

export const tooLargeMessage = (file: FileLike, limits: AttachmentLimits): string =>
  isPdfFile(file)
    ? `${file.name} is ${formatMegabytes(file.size || 0)}. PDFs can be up to ${formatMegabytes(limits.maxPdfBytes)}. ` +
      'Attach only the pages that describe the product, or save a smaller copy.'
    : `${file.name} is ${formatMegabytes(file.size || 0)}. Images can be up to ${formatMegabytes(limits.maxImageBytes)}. ` +
      'Save a smaller or lower-resolution copy and attach that.';

/** The per-message limit as a user is told it, rounded DOWN so it is never promised and then refused. */
export const messageLimitLabel = (limits: AttachmentLimits): string =>
  `${Math.floor(limits.maxMessageBytes / (1024 * 1024))} MB`;

/** What a file adds to a message, as far as the box can tell: a PDF as it is, a photo only if it is sent as is. */
export const bytesSentToModel = (file: FileLike): number => {
  const size = file.size || 0;
  if (isPdfFile(file)) return size;
  return size <= MODEL_IMAGE_PASS_THROUGH_BYTES ? size : 0;
};

export const tooBigForTheModelMessage = (file: FileLike, limits: AttachmentLimits): string =>
  `${file.name} is ${formatMegabytes(file.size || 0)}. The AI can read about ${messageLimitLabel(limits)} of ` +
  'attachments per message. Attach only the pages that describe the product, or save a smaller copy.';

/**
 * What the user is told when the files picked add up to more than one message can carry. `added` of them were
 * staged; `skipped` are the names that did not fit; `totalBytes` is what the whole pick would have come to.
 */
export const tooBigTogetherMessage = (details: {
  limits: AttachmentLimits;
  totalBytes: number;
  added: number;
  skipped: string[];
}): string => {
  const { limits, totalBytes, added, skipped } = details;
  const limit = messageLimitLabel(limits);
  if (added === 0) {
    return (
      `${nameList(skipped)} would bring your attachments to ${formatMegabytes(totalBytes)}, and the AI can read ` +
      `about ${limit} per message. Remove a file or attach a smaller copy.`
    );
  }
  return (
    `Added ${added} of ${added + skipped.length} files. Together they would be ${formatMegabytes(totalBytes)}, ` +
    `and the AI can read about ${limit} per message, so ${nameList(skipped)} ` +
    `${skipped.length === 1 ? "wasn't" : "weren't"} added. Remove a file or attach smaller copies.`
  );
};

/**
 * What the user is told when more files were picked than a message can carry. `added` is how many of them
 * did fit (they are staged); `skipped` are the names that did not.
 */
export const tooManyFilesMessage = (details: { limit: number; added: number; skipped: string[] }): string => {
  const { limit, added, skipped } = details;
  if (added === 0) {
    return `You've already attached ${filesLabel(limit)}, the most one message can carry. Remove one to add another.`;
  }
  return (
    `Added ${added} of ${added + skipped.length} files. The limit is ${filesLabel(limit)} per message, ` +
    `so ${nameList(skipped)} ${skipped.length === 1 ? "wasn't" : "weren't"} added. Remove a file to make room.`
  );
};

/** The attach button's hint: what it is for, and every limit, so nobody learns them by hitting them. */
export const attachmentHint = (limits: AttachmentLimits): string =>
  `Attach a spec sheet PDF or product photo. Up to ${filesLabel(limits.maxFilesPerTurn)} and about ` +
  `${messageLimitLabel(limits)} per message; PDFs up to ${limits.maxPdfPages} pages; ` +
  `images up to ${formatMegabytes(limits.maxImageBytes)}.`;

export type AttachmentCheck = { ok: true } | { ok: false; reason: string };

/** Whether a file may be staged, and when not, the reason to show the user. */
export const checkAttachment = (
  file: FileLike,
  limits: AttachmentLimits = DEFAULT_ATTACHMENT_LIMITS,
): AttachmentCheck => {
  const isPdf = isPdfFile(file);
  if (!isPdf && !isSupportedImageFile(file)) {
    return { ok: false, reason: unsupportedTypeMessage(file) };
  }
  // A PDF the model cannot be sent is refused first: that is the limit a user can really hit, and the
  // message states it, where the upload cap (bigger) would only send them on to the next refusal.
  if (isPdf && (file.size || 0) > limits.maxMessageBytes) {
    return { ok: false, reason: tooBigForTheModelMessage(file, limits) };
  }
  const maxBytes = isPdf ? limits.maxPdfBytes : limits.maxImageBytes;
  if ((file.size || 0) > maxBytes) {
    return { ok: false, reason: tooLargeMessage(file, limits) };
  }
  return { ok: true };
};

/** "Attached spec.pdf" / "Attached 3 files": what the user's bubble shows when they typed nothing. */
export const attachmentSummary = (names: string[]): string =>
  names.length === 1 ? `Attached ${names[0]}` : `Attached ${names.length} files`;

/**
 * The message sent when the user attached files but typed nothing. The backend needs non-empty text for a
 * turn (and for the first turn it becomes the product description), so an attachment-only send would
 * otherwise carry nothing the model could act on.
 */
export const attachmentOnlyMessage = (names: string[]): string =>
  `Please classify the product described in the attached file(s). ${attachmentSummary(names)}.`;

/**
 * The thought-card line shown while the server prepares the user's files, or null with none. A PDF's text is
 * extracted (and a scan is read with OCR) before the turn starts, which can take many seconds with nothing
 * else on screen, so the card says so. The backend narrates the reading itself once the turn is running.
 */
export const attachmentPreparationNote = (refs: Array<{ kind: 'pdf' | 'image' }>): string | null => {
  if (refs.length === 0) return null;
  const hasPdf = refs.some((ref) => ref.kind === 'pdf');
  const hasImage = refs.some((ref) => ref.kind === 'image');
  if (hasPdf && hasImage) return 'Preparing your attached documents and photos…';
  if (hasPdf) {
    return refs.length === 1
      ? 'Extracting the text from your attached document…'
      : 'Extracting the text from your attached documents…';
  }
  return refs.length === 1 ? 'Preparing your attached photo…' : 'Preparing your attached photos…';
};

/**
 * An id for the conversation, used as the upload's conversation id before the server has made a session.
 * It becomes part of the storage path, so it is a plain token (the server accepts letters, digits, "_" and
 * "-", up to 128 characters).
 */
export const newClassifyConversationId = (): string => {
  const cryptoApi = typeof window !== 'undefined' ? (window as any).crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `classify-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
};
